import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, createContext, useContext, useRef } from "react";
import axios from "axios";
import InstallPWA from "@/components/InstallPWA";
import NotificationPrompt from "@/components/NotificationPrompt";
import { ThemeProvider } from "@/context/ThemeContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { subscribeToPush, isPushSupported, getNotificationPermission, setupNotificationSoundHandler } from "@/services/pushNotifications";
import { Capacitor } from '@capacitor/core';
import { liveUpdateService } from "@/services/liveUpdateService";

// Pages
import LandingPage from "@/pages/LandingPage";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import DriverDashboard from "@/pages/driver/DriverDashboard";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import SuperAdminDashboard from "@/pages/superadmin/SuperAdminDashboard";
import CityAdminDashboard from "@/pages/city-admin/CityAdminDashboard";

// Get backend URL - handles web, PWA, and native Capacitor
const getBackendUrl = () => {
  // First check environment variable (set at build time)
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Check if running in Capacitor native app
  if (Capacitor.isNativePlatform()) {
    console.log('[App] Running in Capacitor native platform:', Capacitor.getPlatform());
    return 'https://bizilgo.com';
  }
  
  // For Capacitor web or when env is not set
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    // If running in Capacitor (capacitor:// or file://)
    if (protocol === 'capacitor:' || protocol === 'file:' || protocol === 'ionic:') {
      return 'https://bizilgo.com';
    }
    // For web, use same origin
    if (protocol === 'https:' || protocol === 'http:') {
      return window.location.origin;
    }
  }
  
  // Final fallback
  return 'https://bizilgo.com';
};

const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

console.log('[App] Using backend URL:', BACKEND_URL);

// Create axios instance with default config (for future use)
export const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Set up global axios interceptors (affects all axios calls including direct axios.get/post)
let interceptorsConfigured = false;

const setupGlobalAxiosInterceptors = () => {
  if (interceptorsConfigured) return;
  interceptorsConfigured = true;

  // Request interceptor - add token to all API requests
  axios.interceptors.request.use(
    (config) => {
      // Only add token for API calls to our backend
      if (config.url?.includes('/api/') || config.baseURL?.includes(BACKEND_URL)) {
        const token = localStorage.getItem("biketaxi_token");
        if (token && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Also set up for apiClient
  apiClient.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("biketaxi_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

// Initialize interceptors immediately
setupGlobalAxiosInterceptors();

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("biketaxi_token"));
  const [loading, setLoading] = useState(true);

  // Verify token on load
  useEffect(() => {
    const verifyToken = async () => {
      const storedToken = localStorage.getItem("biketaxi_token");
      if (storedToken) {
        try {
          const res = await axios.get(`${API}/auth/me?token=${storedToken}`);
          setUser(res.data);
          setToken(storedToken);
        } catch (err) {
          // Token verification failed - silent fail, user will be logged out
          localStorage.removeItem("biketaxi_token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
    
    // Setup notification sound handler
    setupNotificationSoundHandler();
    
    // Initialize Live Updates (for native apps)
    liveUpdateService.initialize().catch(console.error);
  }, []);

  const login = (newToken, userData) => {
    localStorage.setItem("biketaxi_token", newToken);
    setToken(newToken);
    setUser(userData);
    
    // Auto-subscribe to push notifications if permission already granted
    if (isPushSupported() && getNotificationPermission() === 'granted' && userData?.id) {
      subscribeToPush(userData.id, userData.role).catch(console.error);
    }
  };

  const logout = () => {
    localStorage.removeItem("biketaxi_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Wrapper for NotificationPrompt that uses Auth context
const NotificationPromptWrapper = () => {
  const { user } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Show notification prompt 3 seconds after user logs in
    if (user && user.role === 'customer') {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  if (!showPrompt || !user) return null;

  return (
    <NotificationPrompt 
      userId={user.id} 
      userType={user.role}
      onClose={() => setShowPrompt(false)}
    />
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrandingProvider>
        <LanguageProvider>
          <AuthProvider>
            <div className="App">
              <BrowserRouter>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route
                    path="/customer/*"
                    element={
                      <ProtectedRoute allowedRoles={["customer", "driver"]}>
                        <CustomerDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/driver/*"
                    element={
                      <ProtectedRoute allowedRoles={["driver"]}>
                        <DriverDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "sub_admin"]}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/superadmin/*"
                    element={
                      <ProtectedRoute allowedRoles={["superadmin", "sub_super_admin"]}>
                        <SuperAdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/city-admin/*"
                    element={
                      <ProtectedRoute allowedRoles={["city_admin"]}>
                        <CityAdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/reset-password" element={<LandingPage />} />
                </Routes>
                <NotificationPromptWrapper />
              </BrowserRouter>
              <Toaster position="top-center" richColors />
              <InstallPWA />
            </div>
          </AuthProvider>
        </LanguageProvider>
      </BrandingProvider>
    </ThemeProvider>
  );
}

export default App;
